import { Option } from "@/types/option";

// ISO 3166-1 alpha-2, plus XK (Kosovo) which the geo lookup returns. Kept in step with the backend list.
export const COUNTRY_CODES: readonly string[] = (
  "AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ " +
  "BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM " +
  "DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS " +
  "GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN " +
  "KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ " +
  "MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM " +
  "PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV " +
  "SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI " +
  "VN VU WF WS XK YE YT ZA ZM ZW"
).split(" ");

export const MIN_PLAYER_AGE = 18;

/** Country options named in the player's language, sorted by name. */
export const getCountryOptions = (language: string): Option[] => {
  let names: Intl.DisplayNames | null = null;
  try {
    names = new Intl.DisplayNames([language || "en", "en"], { type: "region" });
  } catch {
    names = null;
  }
  return COUNTRY_CODES.map((code) => ({ value: code, label: names?.of(code) || code })).sort(
    (a, b) => a.label.localeCompare(b.label, language || "en"),
  );
};

/** True when a YYYY-MM-DD date of birth is at least `minAge` years before today. */
export const isAdultBirthDate = (value: string | undefined, minAge = MIN_PLAYER_AGE): boolean => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const now = new Date();
  let age = now.getFullYear() - year;
  if (now.getMonth() + 1 < month || (now.getMonth() + 1 === month && now.getDate() < day)) age--;
  return age >= minAge;
};

/** A real calendar date in the past, written YYYY-MM-DD. */
export const isValidBirthDate = (value: string | undefined): boolean => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  if (!match) return false;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return (
    date.getUTCFullYear() === Number(match[1]) &&
    date.getUTCMonth() === Number(match[2]) - 1 &&
    date.getUTCDate() === Number(match[3]) &&
    Number(match[1]) >= 1900 &&
    date.getTime() < Date.now()
  );
};

/** Latest birth date an adult can have, for the date input's `max`. */
export const latestAdultBirthDate = (minAge = MIN_PLAYER_AGE): string => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear() - minAge}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};
